// =============================================================================
// src/data/analytics-live-query.ts — Query GraphQL do Analytics (social_listening).
//
// Contrato com o backend AtalaIA (2026-07-24): source Hasura `social_listening`
// com root fields prefixados `sl_` (hasura/apply_social_listening_permissions.py),
// role anon escopada pelo header x-hasura-client-id. ANALYTICS_GRAPHQL_ROOTS
// centraliza TODOS os root fields expostos (troca de naming em 1 lugar); a query
// seleciona só os que têm superfície consumidora nesta fase — sem sentimento,
// sem reach, sem SoV (decisão 2026-07-24; ficam no fallback mock).
// =============================================================================

import { ANALYTICS_GRAPHQL_ROOTS } from './analytics-graphql-roots';

// Re-export de compatibilidade: o mapa vive em analytics-graphql-roots.ts
// desde 2026-07-28 (este arquivo encostava no limite de 500 linhas).
export { ANALYTICS_GRAPHQL_ROOTS } from './analytics-graphql-roots';

/** Tamanho dos buckets do feed. A SELEÇÃO é multi-bucket (decisão 2026-07-25,
 * feedback do usuário: só "12 mais recentes" enterrava o que importa): recentes
 * + top posts por engajamento + top comentários por likes + top por views +
 * top por plataforma (garante conteúdo para CADA filtro de canal do rail),
 * tudo ordenado NO SQL; o front só mescla, deduplica e rankeia. */
export const ANALYTICS_FEED_RECENT_LIMIT = 12;
export const ANALYTICS_FEED_TOP_LIMIT = 12;
export const ANALYTICS_FEED_VIEWS_LIMIT = 8;
export const ANALYTICS_FEED_PLATFORM_LIMIT = 4;

/** Top autores pedidos ao Hasura (ordenado no SQL; a tabela mostra 8). Filtro
 * platform != web: veículos de notícia pertencem à tabela Sites, não à de perfis. */
export const ANALYTICS_TOP_AUTHORS_LIMIT = 12;

/** Fragment compartilhado pelos documentos de feed (dashboard e drill-down). */
export const ANALYTICS_FEED_FIELDS_FRAGMENT = `
  fragment AnalyticsFeedFields on ${ANALYTICS_GRAPHQL_ROOTS.mentionsFeed} {
    content_id
    platform
    content_kind
    content_format
    text_body
    url
    published_at
    author_handle
    author_name
    author_followers
    author_verified
    likes_count
    comments_count
    shares_count
    views_count
    engagement_score
    themes
    actor_names
    actor_slugs
    actor_relations
    actor_relation_pairs
    thread_root_url
    thread_root_author_handle
    thread_root_text_preview
    relevance_verdict
    vip_relation
    text_substance
  }
`;

// A query do dashboard (multi-bucket, gerada por escopo) vive em
// analytics-dashboard-query.ts desde 2026-09-02 — este arquivo guarda o
// fragment, os limites, o drill-down, as mutations e os tipos de linha.

/** Configuração REAL de ingestão (aba Configurações, 021): o que o Dagster usa
 * hoje — temas/palavras-chave (com as queries da busca paga), perfis com a
 * cadência da fila-mestra, recorrências por tipo de alvo e os filtros
 * (léxico de escopo + veículos + autores bloqueados). Somente-leitura. */
export const ANALYTICS_INGESTION_QUERY = `
  query AnalyticsIngestionConfig($clientId: uuid!) {
    watchQueries: ${ANALYTICS_GRAPHQL_ROOTS.watchQueryConfig}(
      where: { client_id: { _eq: $clientId } }
      order_by: { theme_label: asc }
    ) {
      slug
      theme_label
      terms
      hashtags
      platforms
      geo
      is_active
      search_enabled
      search_config
    }
    profiles: ${ANALYTICS_GRAPHQL_ROOTS.ingestionProfile}(
      where: { client_id: { _eq: $clientId } }
      order_by: [{ actor_role: asc }, { actor_name: asc }, { platform: asc }]
    ) {
      actor_slug
      actor_name
      actor_role
      platform
      handle
      is_active
      cadence_min
      tier
      last_run_status
    }
    cadences: ${ANALYTICS_GRAPHQL_ROOTS.ingestionCadence}(
      where: { client_id: { _eq: $clientId } }
      order_by: [{ target_kind: asc }, { platform: asc }]
    ) {
      target_kind
      platform
      cadence_min
      tier
      active_targets
      total_targets
    }
    scopeTerms: ${ANALYTICS_GRAPHQL_ROOTS.ingestionScopeTerm}(
      where: { client_id: { _eq: $clientId } }
      order_by: [{ scope_kind: asc }, { term: asc }]
    ) {
      scope_kind
      term
      is_active
    }
    outletScopes: ${ANALYTICS_GRAPHQL_ROOTS.ingestionOutletScope}(
      where: { client_id: { _eq: $clientId } }
      order_by: [{ outlet_scope: asc }, { outlet_handle: asc }]
    ) {
      outlet_handle
      outlet_scope
    }
    blockedAuthors: ${ANALYTICS_GRAPHQL_ROOTS.authorBlocklist}(
      where: { client_id: { _eq: $clientId } }
      order_by: { blocked_at: desc }
    ) {
      platform
      author_handle
      reason
    }
  }
`;

/**
 * Drill-down por PERSONAGEM (2026-07-25): clicar num ator busca o conteúdo
 * REAL dele no servidor (recentes + top por engajamento), opcionalmente
 * restrito ao canal ativo — a amostra local do feed não limita mais a análise
 * (caso Omar×Instagram: 0 na amostra, 62 no banco).
 */
export const ANALYTICS_ACTOR_FEED_QUERY = `
  query AnalyticsActorFeed($clientId: uuid!, $actorSlugs: [String!]!, $relations: [String!]!, $relationPairs: [String!]!, $rawPlatforms: [String!]!, $topsSinceTs: timestamptz!) {
    actorRecent: ${ANALYTICS_GRAPHQL_ROOTS.mentionsFeed}(
      where: { client_id: { _eq: $clientId }, actor_slugs: { _contains: $actorSlugs }, actor_relations: { _contains: $relations }, actor_relation_pairs: { _contains: $relationPairs }, platform: { _in: $rawPlatforms }, published_at: { _gte: $topsSinceTs } }
      order_by: { published_at: desc }
      limit: ${ANALYTICS_FEED_RECENT_LIMIT}
    ) { ...AnalyticsFeedFields }
    actorTop: ${ANALYTICS_GRAPHQL_ROOTS.mentionsFeed}(
      where: { client_id: { _eq: $clientId }, actor_slugs: { _contains: $actorSlugs }, actor_relations: { _contains: $relations }, actor_relation_pairs: { _contains: $relationPairs }, platform: { _in: $rawPlatforms }, published_at: { _gte: $topsSinceTs } }
      order_by: [{ engagement_score: desc }, { published_at: desc }]
      limit: ${ANALYTICS_FEED_TOP_LIMIT}
    ) { ...AnalyticsFeedFields }
  }
  ${ANALYTICS_FEED_FIELDS_FRAGMENT}
`;

/** Aliases dos buckets do feed na resposta GraphQL (ordem = prioridade do merge). */
export const ANALYTICS_FEED_BUCKET_ALIASES = [
  'recentMentions',
  'topEngagementPosts',
  'topLikedComments',
  'topViewedContent',
  'tiktokTopContent',
  'facebookTopContent',
  'xTopContent',
] as const;

export type AnalyticsFeedBucketAlias = (typeof ANALYTICS_FEED_BUCKET_ALIASES)[number];

/** Mutation de bloqueio de autor (falso positivo curado pelo operador). */
export const ANALYTICS_BLOCK_AUTHOR_MUTATION = `
  mutation AnalyticsBlockAuthor($platform: String!, $authorHandle: String!, $reason: String) {
    ${ANALYTICS_GRAPHQL_ROOTS.insertAuthorBlock}(object: { platform: $platform, author_handle: $authorHandle, reason: $reason }) {
      id
    }
  }
`;

export interface AnalyticsAuthorBlockResult {
  readonly sl_insert_config_author_blocklist_one: { readonly id: string } | null;
}

/** Mutation de escopo por CONTEUDO (017): o que a blocklist de autor nao resolve
 * — portal local monitorado republicando pauta nacional. client_id vem da
 * session var (PRESET), nunca do payload. */
export const ANALYTICS_SCOPE_OVERRIDE_MUTATION = `
  mutation AnalyticsScopeOverride($contentId: uuid!, $verdict: String!, $reason: String) {
    ${ANALYTICS_GRAPHQL_ROOTS.insertScopeOverride}(object: { content_id: $contentId, verdict: $verdict, reason: $reason }) {
      id
    }
  }
`;

export interface AnalyticsScopeOverrideResult {
  readonly sl_insert_config_content_relevance_override_one: { readonly id: string } | null;
}

export interface AnalyticsMentionsDailyRow {
  readonly day: string;
  readonly platform: string;
  readonly content_kind: string;
  readonly mentions: number;
  readonly likes_total: number;
  readonly comments_total: number;
  readonly shares_total: number;
  readonly views_total: number;
}

export interface AnalyticsMentionsFeedRow {
  readonly content_id: string;
  readonly platform: string;
  readonly content_kind: string;
  readonly content_format: string;
  readonly text_body: string | null;
  readonly url: string | null;
  readonly published_at: string | null;
  readonly author_handle: string | null;
  readonly author_name: string | null;
  readonly author_followers: number | null;
  readonly author_verified: boolean | null;
  readonly likes_count: number;
  readonly comments_count: number;
  readonly shares_count: number;
  readonly views_count: number;
  readonly engagement_score: number;
  readonly themes: readonly string[];
  readonly actor_names: readonly string[];
  readonly actor_slugs: readonly string[];
  /** Relação ator↔conteúdo já no vocabulário da UI (016): publicacao/marcacao/mencao. */
  readonly actor_relations: readonly string[];
  readonly actor_relation_pairs: readonly string[];
  /** Contexto da thread (014): post-raiz de comentário/reply; null em post/news. */
  readonly thread_root_url: string | null;
  readonly thread_root_author_handle: string | null;
  readonly thread_root_text_preview: string | null;
  /** Escopo Amazonas (migration 017/018): 'nucleo' | 'contexto'. O backend já NÃO
   * serve 'descartado' — este campo separa o que é cenário político do AM do que é
   * apenas contexto local (ZFM, Arena da Amazônia, esporte amazonense). */
  readonly relevance_verdict: string | null;
  /** 025: relacao com o VIP (ator self): 'publicacao_propria' | 'citacao' |
   * 'comentario_em_post_proprio' | 'nenhuma'. */
  readonly vip_relation: string | null;
  /** 024: substancia do comentario: 'substantivo' | 'reacao' | 'vazio'; null em
   * post/news (nao se aplica) ou comentario ainda nao classificado (≤ 1 tick). */
  readonly text_substance: string | null;
  /** 036 (2026-09-23): impacto para o Omar julgado pelo Jev — 'promotor' |
   * 'neutro' | 'detrator'; null em item julgado antes da 036. Opcional porque só o
   * feed paginado pede (withSentiment); os documentos dos baldes não. */
  readonly sentiment_verdict?: string | null;
  /** Confiança do Jev (0–1). Numeric do Postgres: o Hasura pode serializar como
   * string, por isso o parse defensivo em analyticsImpactFromRow. */
  readonly sentiment_conf?: number | string | null;
}

/** 025: linha da diaria por escopo (mesma soma da v_mentions_daily + 3 booleanos). */
export interface AnalyticsMentionsDailyScopedRow extends AnalyticsMentionsDailyRow {
  readonly is_nucleo: boolean;
  readonly is_vip: boolean;
  readonly is_substantivo: boolean;
}

/** 025: frescor da ingestao por plataforma (eyebrow "atualizado ha X"). */
export interface AnalyticsIngestionFreshnessRow {
  readonly platform: string;
  readonly last_collected_at: string | null;
  readonly last_published_at: string | null;
  readonly collected_24h: number;
  readonly collected_7d: number;
}

export interface AnalyticsAuthorWindowRow {
  readonly author_handle: string | null;
  readonly author_name: string | null;
  readonly platform: string;
  readonly followers_current: number | null;
  readonly mentions: number;
  readonly engagement_score: number;
}

export interface AnalyticsActorPlatformRow {
  readonly actor_slug: string;
  readonly actor_name: string;
  readonly actor_role: string;
  readonly platform: string;
  readonly mentions: number;
}

export interface AnalyticsActorFeedResult {
  readonly actorRecent: readonly AnalyticsMentionsFeedRow[];
  readonly actorTop: readonly AnalyticsMentionsFeedRow[];
}

export interface AnalyticsNewsSourceDailyRow {
  readonly day: string;
  readonly source_name: string;
  readonly mentions: number;
  readonly engagement_score: number;
}

export interface AnalyticsWatchWindowRow {
  readonly watch_slug: string;
  readonly theme_label: string;
  readonly mentions: number;
  readonly interactions: number;
}

export interface AnalyticsRegionWindowRow {
  readonly region_name: string;
  readonly region_kind: string;
  readonly uf: string | null;
  readonly mentions: number;
}

export type AnalyticsGraphqlData = {
  readonly sl_serving_v_mentions_daily: readonly AnalyticsMentionsDailyRow[];
  readonly mentionsDailyScoped?: readonly AnalyticsMentionsDailyScopedRow[];
  readonly ingestionFreshness?: readonly AnalyticsIngestionFreshnessRow[];
  readonly sl_serving_v_author_window_30d: readonly AnalyticsAuthorWindowRow[];
  readonly sl_serving_v_news_source_daily: readonly AnalyticsNewsSourceDailyRow[];
  readonly actorPlatformWindow: readonly AnalyticsActorPlatformRow[];
  readonly watchWindow: readonly AnalyticsWatchWindowRow[];
  readonly regionWindow: readonly AnalyticsRegionWindowRow[];
} & { readonly [Alias in AnalyticsFeedBucketAlias]: readonly AnalyticsMentionsFeedRow[] };

// --- Configuração de ingestão (021, aba Configurações) ----------------------

/** search_config editorial da busca paga (migration 008: banco vence o código). */
export interface AnalyticsWatchSearchPlatformConfig {
  readonly query?: string;
  readonly hashtag?: string;
}

export interface AnalyticsWatchQueryConfigRow {
  readonly slug: string;
  readonly theme_label: string;
  readonly terms: readonly string[];
  readonly hashtags: readonly string[];
  readonly platforms: readonly string[];
  readonly geo: string;
  readonly is_active: boolean;
  readonly search_enabled: boolean;
  readonly search_config: Readonly<Record<string, AnalyticsWatchSearchPlatformConfig>>;
}

export interface AnalyticsIngestionProfileRow {
  readonly actor_slug: string;
  readonly actor_name: string;
  readonly actor_role: string;
  readonly platform: string;
  readonly handle: string;
  readonly is_active: boolean;
  readonly cadence_min: number | null;
  readonly tier: string | null;
  readonly last_run_status: string | null;
}

export interface AnalyticsIngestionCadenceRow {
  readonly target_kind: string;
  readonly platform: string;
  readonly cadence_min: number;
  readonly tier: string;
  readonly active_targets: number;
  readonly total_targets: number;
}

export interface AnalyticsIngestionScopeTermRow {
  readonly scope_kind: string;
  readonly term: string;
  readonly is_active: boolean;
}

export interface AnalyticsIngestionOutletScopeRow {
  readonly outlet_handle: string;
  readonly outlet_scope: string;
}

export interface AnalyticsBlockedAuthorRow {
  readonly platform: string;
  readonly author_handle: string;
  readonly reason: string | null;
}

export interface AnalyticsIngestionGraphqlData {
  readonly watchQueries: readonly AnalyticsWatchQueryConfigRow[];
  readonly profiles: readonly AnalyticsIngestionProfileRow[];
  readonly cadences: readonly AnalyticsIngestionCadenceRow[];
  readonly scopeTerms: readonly AnalyticsIngestionScopeTermRow[];
  readonly outletScopes: readonly AnalyticsIngestionOutletScopeRow[];
  readonly blockedAuthors: readonly AnalyticsBlockedAuthorRow[];
}
